import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { BRAND } from "../../data";
import { MaskedLines, scrollToId } from "./primitives";
const HERO_IMG = "/hero-bg.webp";

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 260]);
  const scale = useTransform(scrollY, [0, 900], [1.05, 1.25]);
  const overlay = useTransform(scrollY, [0, 700], [0.4, 0.7]);

  const go = (id) => scrollToId(id);

  return (
    <section id="top" data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="M Construction skid steer grading a home site in Southwest Florida" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-brand-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/50 to-brand-black/10" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-brand-black to-transparent" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-brand-black/70 to-transparent" />

      <div className="relative z-10 h-full flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="w-10 h-[2px] bg-brand-red" />
          <span className="font-mono-p text-xs uppercase tracking-[0.28em] text-white text-shadow-hero">
            Residential · Commercial · Site Work Specialists
          </span>
        </motion.div>

        <h1 className="font-anton uppercase leading-[0.82] tracking-tight text-[15vw] sm:text-[12vw] lg:text-[9.5vw] text-shadow-hero">
          <MaskedLines
            label="Built on Experience. Driven by Quality."
            lines={[
              <>Built on Experience<span className="text-brand-red">.</span></>,
              <>Driven by Quality<span className="text-brand-red">.</span></>,
            ]}
            lineClass="text-white [&>*]:inline"
            start={0.35}
          />
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-md font-ibm text-base md:text-lg text-white/90 leading-relaxed text-shadow-hero"
          >
            From raw land to finished site — M Construction handles the full scope of site work
            across {BRAND.area}. Clearing, grading, septic, concrete, and everything between.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-quote-btn" onClick={() => go("contact")} className="btn-primary">
              <span>Get a Free Quote</span>
            </button>
            <button data-testid="hero-services-btn" onClick={() => go("services")} className="btn-outline">
              <span>Our Services</span>
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => go("marquee")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 flex items-center gap-2 text-white/50 hover:text-brand-red transition-colors"
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono-p text-xs uppercase tracking-[0.2em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.button>
    </section>
  );
};
